import { randomUUID } from 'node:crypto';
import { BadRequestException, Injectable, NotFoundException, Optional } from '@nestjs/common';
import {
  AddCartItemRequest,
  CartItemSummary,
  CartSummary,
  UpdateCartItemRequest,
} from '@cart/shared-types';
import { AuditService } from '../audit/audit.service';
import { InventoryLedgerService } from '../inventory/inventory-ledger.service';
import { CartRecord, CartRepository, CouponRecord } from './cart.repository';

@Injectable()
export class CartService {
  constructor(
    private readonly cartRepository: CartRepository,
    @Optional()
    private readonly auditService?: AuditService,
    @Optional()
    private readonly ledger?: InventoryLedgerService
  ) {}

  /**
   * How many of this exact thing the shopper can still buy.
   *
   * `product.stock` is the sum across every variant, so validating a line
   * against it let a shopper put six 1 L bottles in the cart when the store
   * held six 500 ml and no 1 L at all. The ledger holds a figure per variant;
   * that is the one a line has to be checked against.
   *
   * Falls back to the product total when there is no ledger (a spec building
   * the service bare) or no variant on the line.
   */
  private async sellableFor(
    productId: string,
    variantId: string | null,
    productStock: number
  ): Promise<number> {
    if (!this.ledger) return productStock;
    return (await this.ledger.sellable(productId, variantId)) ?? productStock;
  }

  async getCart(userId: string): Promise<CartSummary> {
    return this.toSummary(await this.cartRepository.getCart(userId), userId);
  }

  async addItem(userId: string, input: AddCartItemRequest): Promise<CartSummary> {
    this.validateQuantity(input.quantity);

    const product = await this.cartRepository.findProduct(input.productId);

    if (!product || !product.active) {
      throw new NotFoundException('Product is not available');
    }

    // A chosen size prices the line; without one the product's own price
    // stands, which is what a single-size product has always done.
    const variant = input.variantId
      ? await this.cartRepository.findVariant(product.id, input.variantId)
      : undefined;

    if (input.variantId && !variant) {
      throw new NotFoundException('Selected variant is not available');
    }

    const unitPrice = variant?.price ?? product.price;

    const cart = await this.cartRepository.getCart(userId);
    // Two sizes of the same product are two lines, not one: merging them would
    // charge whichever price happened to be added first for both.
    const existing = cart.items.find(
      (item) => item.productId === product.id && (item.variantId ?? null) === (variant?.id ?? null)
    );
    const nextQuantity = (existing?.quantity ?? 0) + input.quantity;

    const sellable = await this.sellableFor(product.id, variant?.id ?? null, product.stock);
    this.assertStock(sellable, nextQuantity);

    // A cart item id must be unique per row, not per product — the same
    // product sits in many different customers' carts simultaneously, so a
    // deterministic id here caused primary-key collisions across carts.
    const newItemId = randomUUID();

    if (existing) {
      existing.quantity = nextQuantity;
      existing.lineTotal = existing.unitPrice * existing.quantity;
      existing.stock = sellable;
    } else {
      cart.items.push({
        id: newItemId,
        productId: product.id,
        productName: product.name,
        productSlug: product.slug,
        unitPrice,
        quantity: input.quantity,
        lineTotal: unitPrice * input.quantity,
        stock: sellable,
        variantId: variant?.id ?? null,
        variantLabel: variant?.label ?? null,
      });
    }

    const summary = await this.toSummary(await this.cartRepository.saveCart(userId, cart), userId);

    await this.auditService?.logChange({
      schemaName: 'cart',
      tableName: 'cart_items',
      recordId: existing?.id ?? newItemId,
      operation: existing ? 'update' : 'create',
      changedColumns: ['productId', 'quantity', 'lineTotal'],
      newValue: summary,
      source: 'CartService.addItem',
    });

    return summary;
  }

  async updateItem(userId: string, itemId: string, input: UpdateCartItemRequest): Promise<CartSummary> {
    this.validateQuantity(input.quantity);

    const cart = await this.cartRepository.getCart(userId);
    const item = cart.items.find((cartItem) => cartItem.id === itemId);

    if (!item) {
      throw new NotFoundException('Cart item was not found');
    }

    const product = await this.cartRepository.findProduct(item.productId);

    if (!product || !product.active) {
      throw new NotFoundException('Product is not available');
    }

    // Re-read the price from whatever the line actually represents. Taking
    // product.price unconditionally would silently re-price a "1 L" line down
    // to the 500 ml price the moment the shopper changed its quantity.
    const variant = item.variantId
      ? await this.cartRepository.findVariant(item.productId, item.variantId)
      : undefined;
    const unitPrice = variant?.price ?? product.price;

    // Checked after the variant is known, so raising the quantity of a 1 L line
    // is limited by the 1 L stock and not by the product-wide total.
    const sellable = await this.sellableFor(item.productId, item.variantId ?? null, product.stock);
    this.assertStock(sellable, input.quantity);

    item.quantity = input.quantity;
    item.unitPrice = unitPrice;
    item.lineTotal = unitPrice * input.quantity;
    item.stock = sellable;
    if (variant) item.variantLabel = variant.label;

    const summary = await this.toSummary(await this.cartRepository.saveCart(userId, cart), userId);

    await this.auditService?.logChange({
      schemaName: 'cart',
      tableName: 'cart_items',
      recordId: itemId,
      operation: 'update',
      changedColumns: ['quantity', 'unitPrice', 'lineTotal'],
      newValue: summary,
      source: 'CartService.updateItem',
    });

    return summary;
  }

  async removeItem(userId: string, itemId: string): Promise<CartSummary> {
    const cart = await this.cartRepository.getCart(userId);
    const nextItems = cart.items.filter((item) => item.id !== itemId);

    if (nextItems.length === cart.items.length) {
      throw new NotFoundException('Cart item was not found');
    }

    const summary = await this.toSummary(await this.cartRepository.saveCart(userId, { ...cart, items: nextItems }), userId);

    await this.auditService?.logChange({
      schemaName: 'cart',
      tableName: 'cart_items',
      recordId: itemId,
      operation: 'delete',
      source: 'CartService.removeItem',
    });

    return summary;
  }

  async clear(userId: string): Promise<CartSummary> {
    const cart = await this.cartRepository.getCart(userId);

    const summary = await this.toSummary(
      await this.cartRepository.saveCart(userId, { ...cart, items: [], couponCode: undefined, discountTotal: 0 }),
      userId
    );

    await this.auditService?.logChange({
      schemaName: 'cart',
      tableName: 'carts',
      recordId: cart.id,
      operation: 'update',
      changedColumns: ['items'],
      newValue: summary,
      source: 'CartService.clear',
    });

    return summary;
  }

  async applyCoupon(userId: string, code: string): Promise<CartSummary> {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) {
      throw new BadRequestException('Enter a coupon code');
    }

    const cart = await this.cartRepository.getCart(userId);
    const subtotal = cart.items.reduce((sum, item) => sum + item.lineTotal, 0);

    const coupon = await this.cartRepository.findCouponByCode(trimmed);
    if (!coupon) {
      throw new NotFoundException('Coupon code not found');
    }

    // Adding a code to one already applied only stacks them when both sides
    // agree to it (this code is stackable AND every already-applied code is
    // too) — otherwise the new code replaces whatever was there, same as
    // typing over a single-code field.
    const existingCodes = cart.couponCode ? cart.couponCode.split(',').filter(Boolean) : [];
    let candidateCodes = [trimmed];

    if (existingCodes.length > 0 && !existingCodes.includes(trimmed)) {
      const existingCoupons = (
        await Promise.all(existingCodes.map((c) => this.cartRepository.findCouponByCode(c)))
      ).filter((c): c is CouponRecord => !!c);

      if (coupon.stackable && existingCoupons.every((c) => c.stackable)) {
        candidateCodes = [...existingCodes, trimmed];
      }
    }

    const candidateCoupons = (
      await Promise.all(candidateCodes.map((c) => this.cartRepository.findCouponByCode(c)))
    ).filter((c): c is CouponRecord => !!c);

    const discount = await this.computeValidDiscount(candidateCoupons, subtotal, userId);
    if (discount === null) {
      throw new BadRequestException('This coupon is no longer valid');
    }

    const updated = await this.cartRepository.saveCart(userId, {
      ...cart,
      couponCode: candidateCodes.join(','),
      discountTotal: discount,
    });
    return this.toSummary(updated, userId);
  }

  async removeCoupon(userId: string): Promise<CartSummary> {
    const cart = await this.cartRepository.getCart(userId);
    const updated = await this.cartRepository.saveCart(userId, { ...cart, couponCode: undefined, discountTotal: 0 });
    return this.toSummary(updated, userId);
  }

  private validateQuantity(quantity: number): void {
    if (!Number.isInteger(quantity) || quantity < 1) {
      throw new BadRequestException('quantity must be at least 1');
    }
  }

  private assertStock(stock: number, quantity: number): void {
    if (quantity > stock) {
      throw new BadRequestException('quantity exceeds available stock');
    }
  }

  // Same rules checkout.service.ts's resolveDiscount() applies to these same
  // DiscountCode rows — keeps "does this code work" consistent whether it's
  // being previewed in the cart or actually charged at checkout. Percentage
  // `value` is basis points (1000 = 10%), matching that existing convention.
  // Multiple coupons (stacked) are only valid together if every one of them
  // is individually marked stackable.
  private async computeValidDiscount(
    coupons: CouponRecord[],
    subtotal: number,
    userId: string
  ): Promise<number | null> {
    if (coupons.length === 0) return null;
    if (coupons.length > 1 && coupons.some((c) => !c.stackable)) return null;

    const now = Date.now();
    let isNewCustomer: boolean | null = null;

    for (const coupon of coupons) {
      if (!coupon.isActive) return null;
      if (coupon.startsOn && coupon.startsOn.getTime() > now) return null;
      if (coupon.endsOn && coupon.endsOn.getTime() < now) return null;
      if (coupon.usageLimit != null && coupon.usedCount >= coupon.usageLimit) return null;
      if (coupon.minOrderAmount != null && subtotal < coupon.minOrderAmount) return null;

      if (coupon.customerEligibility === 'new_customers') {
        if (isNewCustomer === null) {
          isNewCustomer = (await this.cartRepository.countCustomerOrders(userId)) === 0;
        }
        if (!isNewCustomer) return null;
      }
    }

    let total = 0;
    for (const coupon of coupons) {
      let raw =
        coupon.type === 'percentage'
          ? Math.round((subtotal * coupon.value) / 10000)
          : coupon.type === 'fixed'
          ? coupon.value
          : 0; // free_shipping — no line-item discount here, shipping itself is waived at checkout
      if (coupon.maxDiscountAmount != null) raw = Math.min(raw, coupon.maxDiscountAmount);
      total += raw;
    }

    return Math.min(total, subtotal);
  }

  private async toSummary(cart: CartRecord, userId: string): Promise<CartSummary> {
    const items = cart.items.map((item): CartItemSummary => ({
      ...item,
      lineTotal: item.unitPrice * item.quantity,
    }));
    const subtotal = items.reduce((sum, item) => sum + item.lineTotal, 0);

    let couponCode: string | undefined;
    let discountTotal = 0;

    if (cart.couponCode) {
      const codes = cart.couponCode.split(',').filter(Boolean);
      const coupons = (
        await Promise.all(codes.map((c) => this.cartRepository.findCouponByCode(c)))
      ).filter((c): c is CouponRecord => !!c);
      const discount = coupons.length === codes.length
        ? await this.computeValidDiscount(coupons, subtotal, userId)
        : null;
      if (discount !== null) {
        couponCode = cart.couponCode;
        discountTotal = discount;
      }
    }

    return {
      id: cart.id,
      items,
      subtotal,
      totalItems: items.reduce((sum, item) => sum + item.quantity, 0),
      couponCode,
      discountTotal,
      total: Math.max(0, subtotal - discountTotal),
    };
  }
}
